import {createSlice, PayloadAction} from '@reduxjs/toolkit'
import {RootState} from '../../app/rootReducer'
import {
    fetchAllDealers,
    fetchConfigurationPromo,
    fetchMyDealerBranches,
    fetchMyDealerPromotions,
} from '../../app/thunk'
import {DealerData} from '../../types/dealer'

export const dealerSlice = createSlice({
    name: 'dealers',
    initialState: {
        allDealers: [],
        selected: null,
        myDealerCode: '',
        myDealerBranches: [],
        myDealerPromotions: [],
        allDealersRequestId: null as string | null,
        branchesRequestId: null as string | null,
        promotionsRequestId: null as string | null,
        myDealerBranchesLoading: false,
        changePreselectedDealerData: false,
    },
    reducers: {
        setDealers(
            state: RootState,
            action: PayloadAction<DealerData[]>
        ): void {
            state.allDealers = action.payload
        },
        setSelectedDealerData(
            state: RootState,
            action: PayloadAction<DealerData | null>
        ): void {
            state.selected = action.payload
        },
        setMyDealerCode(
            state: RootState,
            action: PayloadAction<string | null>
        ): void {
            if (state.myDealerCode !== action.payload) {
                state.myDealerBranches = []
                state.myDealerPromotions = []
                state.selected = null
                state.branchesRequestId = null
                state.promotionsRequestId = null
                state.myDealerBranchesLoading = false
            }
            state.myDealerCode = action.payload
        },
        changePreselectedDealer(
            state: RootState,
            action: PayloadAction<boolean | null>
        ): void {
            state.changePreselectedDealerData = action.payload
        },
        setMyDealerBranches(
            state: RootState,
            action: PayloadAction<DealerData[]>
        ): void {
            state.myDealerBranches = action.payload
            state.branchesRequestId = null
            state.myDealerBranchesLoading = false
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchConfigurationPromo.pending, (state: RootState) => {
                state.allDealers = []
                state.allDealersRequestId = null
                state.myDealerBranches = []
                state.selected = null
                state.branchesRequestId = null
                state.myDealerBranchesLoading = false
            })
            .addCase(fetchAllDealers.pending, (state: RootState, action) => {
                state.allDealers = []
                state.allDealersRequestId = action.meta.requestId
            })
            .addCase(fetchAllDealers.fulfilled, (state: RootState, action) => {
                if (state.allDealersRequestId === action.meta.requestId) {
                    state.allDealers = action.payload
                    state.allDealersRequestId = null
                }
            })
            .addCase(fetchAllDealers.rejected, (state: RootState, action) => {
                if (state.allDealersRequestId === action.meta.requestId) {
                    state.allDealersRequestId = null
                }
            })
            .addCase(
                fetchMyDealerBranches.pending,
                (state: RootState, action) => {
                    state.branchesRequestId = action.meta.requestId
                    state.myDealerBranches = []
                    state.selected = null
                    state.myDealerBranchesLoading = true
                }
            )
            .addCase(
                fetchMyDealerBranches.fulfilled,
                (state: RootState, action) => {
                    if (
                        state.branchesRequestId === action.meta.requestId &&
                        state.myDealerCode === action.meta.arg
                    ) {
                        state.myDealerBranches = action.payload
                        state.branchesRequestId = null
                        state.myDealerBranchesLoading = false
                    }
                }
            )
            .addCase(
                fetchMyDealerBranches.rejected,
                (state: RootState, action) => {
                    if (state.branchesRequestId === action.meta.requestId) {
                        state.branchesRequestId = null
                        state.myDealerBranchesLoading = false
                    }
                }
            )
            .addCase(
                fetchMyDealerPromotions.pending,
                (state: RootState, action) => {
                    state.promotionsRequestId = action.meta.requestId
                    state.myDealerPromotions = []
                }
            )
            .addCase(
                fetchMyDealerPromotions.fulfilled,
                (state: RootState, action) => {
                    if (
                        state.promotionsRequestId === action.meta.requestId &&
                        state.myDealerCode === action.meta.arg
                    ) {
                        state.myDealerPromotions = action.payload
                        state.promotionsRequestId = null
                    }
                }
            )
            .addCase(
                fetchMyDealerPromotions.rejected,
                (state: RootState, action) => {
                    if (state.promotionsRequestId === action.meta.requestId) {
                        state.promotionsRequestId = null
                    }
                }
            )
    },
})

export const {
    setSelectedDealerData,
    setMyDealerCode,
    changePreselectedDealer,
    setMyDealerBranches,
} = dealerSlice.actions

export default dealerSlice.reducer
